/**
 * Client half of Arb.Web/Attributes/PostcodeRequiredForCountryAttribute.cs: a postcode is required
 * unless a country that doesn't use UK-style postcodes has been chosen. Keep the two in step.
 *
 * The server renders the rule as:
 *   data-val-postcoderequired="<message>"
 *   data-val-postcoderequired-countryproperty="AddressCountryOfResidence"
 *   data-val-postcoderequired-countries="United Kingdom|Guernsey|Isle of Man|Jersey"
 */
export const POSTCODE_REQUIRED_RULE = 'postcoderequired';

/**
 * The country field is a sibling property of the postcode, so it shares the postcode's name prefix
 * (e.g. "Address.AddressPostcode" -> "Address.AddressCountryOfResidence").
 */
export function findCountryField(postcodeField: HTMLInputElement, countryProperty: string): HTMLSelectElement | HTMLInputElement | null {
    const lastDot = postcodeField.name.lastIndexOf('.');
    const name = lastDot >= 0 ? `${postcodeField.name.substring(0, lastDot)}.${countryProperty}` : countryProperty;

    const root: ParentNode = postcodeField.form ?? document;
    return root.querySelector<HTMLSelectElement | HTMLInputElement>(`[name="${CSS.escape(name)}"]`);
}

/** No country chosen yet counts as required, matching the server. */
export function isPostcodeRequired(country: string | null | undefined, countries: string | null | undefined): boolean {
    const value = (country ?? '').trim().toLowerCase();
    if (!value) return true;

    return (countries ?? '')
        .split('|')
        .some(c => c.trim().toLowerCase() === value);
}

export function isPostcodeValid(postcodeField: HTMLInputElement, countryProperty: string, countries: string): boolean {
    if (postcodeField.value.trim() !== '') return true;

    return !isPostcodeRequired(findCountryField(postcodeField, countryProperty)?.value, countries);
}
